import type { Logger } from "@expert/logging";

import { useCallback, useEffect, useMemo, useRef } from "react";
import useWebSocket from "react-use-websocket";

import type { GaiaWebSocketResponse, SendJsonMessage } from "../types";
import type { ShadowComparator } from "./ShadowComparator";

import { type ControlMessage, subscriptionKey } from "../controlMessages";
import { GaiaWebSocketEvent } from "../types";

const heartbeatInterval = 20000;
const heartbeatTimeout = 90000;
// Cap mirrored sends buffered while the shadow socket is connecting, so a shadow
// backend that never opens cannot grow the buffer without bound over a long session.
const maxSendBuffer = 200;

interface UseGaiaShadowWebsocketParams {
    enabled: boolean;
    shadowUrl: string | undefined;
    application: string;
    token: string;
    identity: string;
    comparator: ShadowComparator | null;
    /** Set true while the shadow socket is open so the caller only compares when Simplr is connected. */
    connectedRef: { current: boolean };
    logger: Logger;
}

interface UseGaiaShadowWebsocketResult {
    sendToShadow: SendJsonMessage;
}

/**
 * Opens a second, *shadow* websocket connection to the Simplr backend that runs
 * alongside the live GAIA connection (dark launch). It authorizes itself on open,
 * mirrors the messages the live connection sends (so it receives equivalent server
 * traffic), and feeds every inbound message into the {@link ShadowComparator}.
 *
 * It never emits on the GAIA event bus and never drives the UI — the only output is
 * the comparison results produced by the comparator. When `enabled` is false the
 * hook passes a `null` url to `useWebSocket`, so no connection is ever opened.
 */
export function useGaiaShadowWebsocket({
    enabled,
    shadowUrl,
    application,
    token,
    identity,
    comparator,
    connectedRef,
    logger: loggerProp,
}: UseGaiaShadowWebsocketParams): UseGaiaShadowWebsocketResult {
    const logger = useMemo(
        () => loggerProp.child({ module: "useGaiaShadowWebsocket", supportTeam: "solve" }),
        [loggerProp],
    );

    const url = enabled && shadowUrl ? `${shadowUrl}?application=${application}&token=${token}` : null;

    const isAuthenticatedRef = useRef(false);
    const sendBufferRef = useRef<Parameters<SendJsonMessage>[]>([]);
    // Active subscriptions, so the shadow can re-subscribe itself if it reconnects independently
    // of the live GAIA socket (whose app-driven resubscribe would not fire in that case).
    const subscriptionsRef = useRef<Map<string, Parameters<SendJsonMessage>>>(new Map());

    const { sendJsonMessage } = useWebSocket(
        url,
        {
            heartbeat: {
                interval: heartbeatInterval,
                timeout: heartbeatTimeout,
                message: JSON.stringify({ action: "heartbeat", correlationId: identity }),
            },
            shouldReconnect: () => true,
            reconnectAttempts: 10,
            reconnectInterval: 2000,
            share: false,
            onOpen: () => {
                logger.info("Simplr shadow | websocket open");
                // Only authorize here. Subscriptions and buffered sends are flushed from
                // onMessage once the server acknowledges auth with `authorization-success` —
                // sending them here, before that ack, would race ahead of the auth handshake
                // and risk Simplr dropping or ignoring them.
                isAuthenticatedRef.current = false;
                connectedRef.current = true;
                sendJsonMessage({ action: "authorize", token, correlationId: identity });
            },
            onClose: () => {
                isAuthenticatedRef.current = false;
                connectedRef.current = false;
            },
            onMessage: (event: MessageEvent) =>
                handleShadowMessage({
                    event,
                    comparator,
                    logger,
                    isAuthenticatedRef,
                    onAuthenticated: () => {
                        for (const args of subscriptionsRef.current.values()) sendJsonMessage(...args);
                        const buffered = sendBufferRef.current;
                        sendBufferRef.current = [];
                        for (const args of buffered) sendJsonMessage(...args);
                    },
                }),
            onError: (event: Event) => logger.warn({ error: event.type }, "Simplr shadow | websocket error"),
        },
        Boolean(url),
    );

    // When the shadow is turned off, drop any buffered sends so they cannot leak into a later session.
    useEffect(() => {
        if (!enabled) {
            sendBufferRef.current = [];
            subscriptionsRef.current.clear();
            isAuthenticatedRef.current = false;
            connectedRef.current = false;
        }
    }, [enabled]);

    const sendToShadow = useCallback<SendJsonMessage>(
        (message, keep) => {
            if (!enabled) return;

            // Track subscriptions so they can be replayed on reconnect. Subscribes are never
            // buffered — they live in `subscriptionsRef` and are (re)sent once auth succeeds
            // on every (re)connect (see `onAuthenticated` above).
            const control = message as ControlMessage | null;
            const key = subscriptionKey(control);
            if (control?.action === "subscribe" && key) {
                subscriptionsRef.current.set(key, [message, keep]);
                if (isAuthenticatedRef.current) sendJsonMessage(message, keep);
                return;
            }
            if (control?.action === "unsubscribe" && key) {
                subscriptionsRef.current.delete(key);
                if (isAuthenticatedRef.current) sendJsonMessage(message, keep);
                return;
            }

            if (isAuthenticatedRef.current) {
                sendJsonMessage(message, keep);
            } else {
                sendBufferRef.current.push([message, keep]);
                if (sendBufferRef.current.length > maxSendBuffer) {
                    sendBufferRef.current.shift();
                }
            }
        },
        [enabled, sendJsonMessage],
    );

    return { sendToShadow };
}

interface HandleShadowMessageParams {
    event: MessageEvent;
    comparator: ShadowComparator | null;
    logger: Logger;
    isAuthenticatedRef: { current: boolean };
    onAuthenticated: () => void;
}

function handleShadowMessage({
    event,
    comparator,
    logger,
    isAuthenticatedRef,
    onAuthenticated,
}: HandleShadowMessageParams): void {
    try {
        const data = JSON.parse(event.data as string) as GaiaWebSocketResponse;
        if (!data?.name) return;
        logger.debug({ eventName: data.name }, "Simplr shadow | message received");
        if (data.name === GaiaWebSocketEvent.AuthorizationSuccess && !isAuthenticatedRef.current) {
            isAuthenticatedRef.current = true;
            logger.info("Simplr shadow | websocket authorized");
            onAuthenticated();
        }
        comparator?.record("simplr", data);
    } catch (error: unknown) {
        logger.error({ error }, "Simplr shadow | message handler error");
    }
}
